<template>
  <div class="translation-toolbar-clara-picker" :class="classModifiers">
    <p
      v-if="statusBesideActivator"
      class="translation-toolbar-clara-picker__status"
      :class="statusModifiers"
      role="status"
      part="status"
    >
      {{ status.text }}
    </p>

    <sp-menu
      class="translation-toolbar-clara-picker__menu"
      location="bottom-right"
      close-on-content-click="false"
      :label="pickerTitle"
      :open="pickerOpen"
      @update-open="onPickerOpen"
    >
      <sp-button
        slot="activator"
        class="translation-toolbar-clara-picker__activator"
        variant="text"
        size="small"
        prepend-icon="sparkles"
        aria-haspopup="dialog"
        :aria-expanded="pickerOpen"
        :disabled="busy"
        :data-testid="TestId.admin.translationToolbarTranslate"
      >
        {{ t("translate") }}
      </sp-button>

      <form
        class="translation-toolbar-clara-picker__form"
        :role="dialogRole"
        :aria-labelledby="dialogLabelledBy"
        @submit.prevent="requestTranslations"
      >
        <p :id="TITLE_ID" class="translation-toolbar-clara-picker__title">{{ pickerTitle }}</p>

        <p class="translation-toolbar-clara-picker__warning" role="note">
          {{ t("unsaved_warning") }}
        </p>

        <div class="translation-toolbar-clara-picker__columns">
          <fieldset class="translation-toolbar-clara-picker__group">
            <legend class="translation-toolbar-clara-picker__legend">{{ t("fields_label") }}</legend>
            <sp-checkbox
              class="translation-toolbar-clara-picker__all"
              :label="t('all_fields')"
              :checked.prop="allFieldsSelected"
              @update-checked="onToggleAllFields"
            ></sp-checkbox>
            <sp-checkbox
              v-for="field in fields"
              :key="field.attribute"
              class="translation-toolbar-clara-picker__field"
              :label="field.label"
              :value="field.attribute"
              :checked.prop="field.selected"
              :data-testid="TestId.admin.translationToolbarTranslateField"
              :data-attribute="field.attribute"
              @update-checked="onToggleField"
            ></sp-checkbox>
          </fieldset>

          <fieldset class="translation-toolbar-clara-picker__group">
            <legend class="translation-toolbar-clara-picker__legend">{{ t("languages_label") }}</legend>
            <sp-checkbox
              class="translation-toolbar-clara-picker__all"
              :label="t('all_languages')"
              :checked.prop="allLocalesSelected"
              @update-checked="onToggleAllLocales"
            ></sp-checkbox>
            <sp-checkbox
              v-for="language in languageOptions"
              :key="language.locale"
              class="translation-toolbar-clara-picker__language"
              :label="language.name"
              :value="language.locale"
              :checked.prop="language.selected"
              :data-testid="TestId.admin.translationToolbarTranslateLocale"
              :data-locale="language.locale"
              @update-checked="onToggleLocale"
            ></sp-checkbox>
          </fieldset>
        </div>

        <sp-switch
          class="translation-toolbar-clara-picker__replace"
          :label="t('replace_label')"
          :checked.prop="replace"
          :data-testid="TestId.admin.translationToolbarTranslateReplace"
          @update-checked="onToggleReplace"
        ></sp-switch>

        <p
          v-if="statusInForm"
          class="translation-toolbar-clara-picker__status"
          :class="statusModifiers"
          role="status"
          part="status"
        >
          {{ status.text }}
        </p>

        <div class="translation-toolbar-clara-picker__actions">
          <sp-button variant="text" size="small" @click="closePicker">{{ t("cancel") }}</sp-button>
          <sp-button
            type="submit"
            size="small"
            :disabled="submitDisabled"
            :data-testid="TestId.admin.translationToolbarTranslateSubmit"
          >
            {{ t("submit") }}
          </sp-button>
        </div>
      </form>
    </sp-menu>
  </div>
</template>

<script setup>
/**
 * The page-wide Clara action: a form that picks which of the page's translatable fields and which
 * languages Clara fills, whether it replaces translations that exist, and posts the request. The
 * fields come from the translation page store. A status line reports the answer.
 */
import { computed, ref } from "vue";

import { TestId } from "#/test-ids";
import { useTranslationPageStore } from "#ce/components/SpTranslatableField/store/translation-page";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { createMachineTranslationsApi } from "./api/machine-translations";
import { t } from "./i18n";

const TITLE_ID = "translation-toolbar-clara-picker-title";

const props = defineProps({
  defaultLanguageName: { type: String, required: true },
  languages: { type: Array, required: true },
  resourceType: { type: String, default: undefined },
  resourceId: { type: [String, Number], default: undefined },
  translateUrl: { type: String, required: true },
});

const emit = defineEmits(["translations-requested"]);

const page = useTranslationPageStore();

const pickerTitle = computed(() => t("picker_title", { language: props.defaultLanguageName }));

// Above the sm breakpoint sp-menu shows the form in a plain popup, so the form is the dialog the
// activator announces. Below it the bottom sheet is the dialog and carries the title itself.
const { isGtSmScreen } = useBreakpoints();
const dialogRole = computed(() => (isGtSmScreen.value ? "dialog" : undefined));
const dialogLabelledBy = computed(() => (isGtSmScreen.value ? TITLE_ID : undefined));

const pickerOpen = ref(false);
const selectedAttributes = ref([]);
const selectedLocales = ref([]);
const replace = ref(false);

const attributeNames = computed(() => page.fields.map((field) => field.attribute));
const localeNames = computed(() => props.languages.map((language) => language.locale));

const fields = computed(() =>
  page.fields.map((field) => ({
    attribute: field.attribute,
    label: field.label ?? field.attribute,
    selected: selectedAttributes.value.includes(field.attribute),
  })),
);

const languageOptions = computed(() =>
  props.languages.map((language) => ({
    ...language,
    selected: selectedLocales.value.includes(language.locale),
  })),
);

const allFieldsSelected = computed(
  () => attributeNames.value.length > 0 && selectedAttributes.value.length === attributeNames.value.length,
);

const allLocalesSelected = computed(
  () => localeNames.value.length > 0 && selectedLocales.value.length === localeNames.value.length,
);

function resetSelection() {
  selectedAttributes.value = [...attributeNames.value];
  selectedLocales.value = [...localeNames.value];
  replace.value = false;
}

function onPickerOpen({ detail }) {
  const [open] = detail;
  pickerOpen.value = open;

  if (open) {
    resetSelection();
  }
}

function closePicker() {
  pickerOpen.value = false;
}

function toggled(selection, value, checked) {
  const without = selection.filter((selected) => selected !== value);

  return checked ? [...without, value] : without;
}

function onToggleAllFields({ detail }) {
  const [checked] = detail;
  selectedAttributes.value = checked ? [...attributeNames.value] : [];
}

function onToggleField({ currentTarget, detail }) {
  const [checked] = detail;
  selectedAttributes.value = toggled(selectedAttributes.value, currentTarget.dataset.attribute, checked);
}

function onToggleAllLocales({ detail }) {
  const [checked] = detail;
  selectedLocales.value = checked ? [...localeNames.value] : [];
}

function onToggleLocale({ currentTarget, detail }) {
  const [checked] = detail;
  selectedLocales.value = toggled(selectedLocales.value, currentTarget.dataset.locale, checked);
}

function onToggleReplace({ detail }) {
  const [checked] = detail;
  replace.value = checked;
}

const busy = ref(false);
const submitDisabled = computed(
  () => busy.value || selectedAttributes.value.length === 0 || selectedLocales.value.length === 0,
);
const status = ref({ text: "", error: false });

async function requestTranslations() {
  if (submitDisabled.value) {
    return;
  }

  const request = {
    attributeNames: [...selectedAttributes.value],
    locales: [...selectedLocales.value],
    replace: replace.value,
  };
  busy.value = true;
  status.value = { text: t("requesting"), error: false };

  try {
    const api = createMachineTranslationsApi({
      translateUrl: props.translateUrl,
      resourceType: props.resourceType,
      resourceId: props.resourceId,
    });
    const data = await api.requestTranslations(request);

    status.value = { text: data?.message ?? "", error: false };
    pickerOpen.value = false;
    emit("translations-requested", { ...request, count: data?.count ?? 0 });
  } catch (error) {
    status.value = { text: error?.message || t("request_failed"), error: true };
  } finally {
    busy.value = false;
  }
}

// Below the sm breakpoint the form opens in a bottom sheet that covers the activator, so the status
// shows inside the form while the sheet is open.
const statusInForm = computed(() => Boolean(status.value.text) && pickerOpen.value && !isGtSmScreen.value);
const statusBesideActivator = computed(() => Boolean(status.value.text) && !statusInForm.value);

const classModifiers = computed(() => ({ "--is-busy": busy.value }));
const statusModifiers = computed(() => ({ "--is-error": status.value.error }));
</script>

<style lang="scss" scoped>
.translation-toolbar-clara-picker {
  --menu-padding: var(--sp-comp-translation-toolbar-menu-padding, var(--sp-ref-scale-8));
  --menu-min-width: var(--sp-comp-translation-toolbar-menu-min-width, 14rem);
  --status-color: var(--sp-comp-translation-toolbar-status-color, var(--sp-sys-color-on-surface));
  --status-error-color: var(--sp-comp-translation-toolbar-status-error-color, var(--sp-sys-color-danger));
  --warning-color: var(--sp-comp-translation-toolbar-warning-color, var(--sp-sys-color-on-surface-variant));

  display: flex;
  align-items: center;
  gap: var(--sp-ref-scale-8);

  &__menu {
    --sp-comp-menu-min-width: var(--menu-min-width);
    --sp-comp-menu-width: max-content;

    flex: 0 0 auto;
    width: auto;
  }

  &__activator {
    white-space: nowrap;
  }

  &__status {
    margin: 0;
    color: var(--status-color);
    font-size: var(--sp-ref-font-size-small);

    &.--is-error {
      color: var(--status-error-color);
    }
  }

  &__form {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-8);
    min-width: var(--menu-min-width);
    padding: var(--menu-padding);
  }

  &__title {
    margin: 0;
    font-weight: var(--sp-ref-font-weight-medium);
    white-space: nowrap;
  }

  &__warning {
    max-width: 32rem;
    margin: 0;
    color: var(--warning-color);
    font-size: var(--sp-ref-font-size-small);
  }

  &__columns {
    display: flex;
    gap: var(--sp-ref-scale-24);
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: var(--sp-ref-scale-4);
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  &__legend {
    padding: 0;
    margin-block-end: var(--sp-ref-scale-4);
    font-weight: var(--sp-ref-font-weight-medium);
  }

  &__replace {
    max-width: 32rem;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--sp-ref-scale-8);
    margin-block-start: var(--sp-ref-scale-8);
  }
}
</style>
